import BrandMark from "@/components/BrandMark";

/**
 * Official "NoJo Enterprise" wordmark lockup.
 *
 * Layout (left → right):
 *   [BrandMark icon]  ⎮  [Stacked wordmark]
 *
 *   The stacked wordmark uses:
 *     • Fraunces — a modern editorial serif, optical-size aware
 *       (premium-corporate feel: Bloomberg / Hermès / NYT-grade)
 *     • A tracked, uppercase "ENTERPRISE" tagline below
 *
 * Variants:
 *   - "header" (default) — compact, single-line stack used in the site header
 *   - "footer"           — same look, slightly larger, used in the footer
 *   - "hero"             — large, for use inside content hero treatments
 *
 * The whole component is presentational — give it a `to` Link wrapper
 * from the caller.
 */
export default function Wordmark({
  size = "header",
  className = "",
  showSub = true,
  tone = "navy",
}) {
  const palette = {
    navy: { primary: "#0F1D2D", sub: "#475569", divider: "rgba(15,29,45,0.18)" },
    white: { primary: "#FFFFFF", sub: "rgba(255,255,255,0.7)", divider: "rgba(255,255,255,0.25)" },
  }[tone] || { primary: "#0F1D2D", sub: "#475569", divider: "rgba(15,29,45,0.18)" };

  const config = {
    header: {
      iconSize: 38,
      primary: "text-[20px] sm:text-[22px] md:text-[24px]",
      sub: "text-[8.5px] sm:text-[9px]",
      gap: "gap-2.5 sm:gap-3",
      dividerH: "h-6 md:h-7",
      subMt: "mt-1",
    },
    footer: {
      iconSize: 44,
      primary: "text-[24px] md:text-[26px]",
      sub: "text-[11px] md:text-[12px]",
      gap: "gap-3",
      dividerH: "h-7 md:h-8",
      subMt: "mt-2",
    },
    hero: {
      iconSize: 56,
      primary: "text-[34px] md:text-[40px]",
      sub: "text-[12px] md:text-[13px]",
      gap: "gap-4",
      dividerH: "h-10 md:h-12",
      subMt: "mt-2.5",
    },
  }[size] || {};

  return (
    <span className={["inline-flex items-center", config.gap, className].join(" ")} aria-label="NoJo Enterprise">
      <BrandMark size={config.iconSize} className="shrink-0" />
      <span
        aria-hidden
        className={["w-px self-center shrink-0", config.dividerH].join(" ")}
        style={{ backgroundColor: palette.divider }}
      />
      <span className="flex flex-col leading-tight min-w-0">
        <span
          className={["font-wordmark whitespace-nowrap leading-[1.1]", config.primary].join(" ")}
          style={{ color: palette.primary }}
        >
          NoJo Enterprise
        </span>
        {showSub && (
          <span
            className={["font-wordmark-sub whitespace-normal sm:whitespace-nowrap", config.sub, config.subMt].join(" ")}
            style={{ color: palette.sub }}
          >
            Mobility · Records · Digital Infrastructure
          </span>
        )}
      </span>
    </span>
  );
}
